import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Soft, tasteful floral pattern built entirely from inline SVG shapes — no
// external photo/licensing concerns, just a light, elegant "flowery white
// scenery" backdrop for the sign-in card.
function FloralBackground() {
  const flower = (fill) => (
    <g>
      {[0, 72, 144, 216, 288].map((angle) => (
        <ellipse
          key={angle}
          cx="0" cy="-7" rx="4.2" ry="6.4"
          fill={fill}
          opacity="0.75"
          transform={`rotate(${angle})`}
        />
      ))}
      <circle r="3.2" fill="#f6e7b8" opacity="0.9" />
    </g>
  );

  return (
    <svg className="login-floral-bg" viewBox="0 0 240 240" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <pattern id="floralTile" width="120" height="120" patternUnits="userSpaceOnUse">
          <rect width="120" height="120" fill="#fffdf9" />
          <path d="M10 100 Q 20 70 15 40" stroke="#bcd2b2" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M95 110 Q 100 80 90 55" stroke="#bcd2b2" strokeWidth="2" fill="none" opacity="0.6" />
          <g transform="translate(20,35) scale(1.1)">{flower('#e7b8c2')}</g>
          <g transform="translate(95,55) scale(0.9) rotate(20)">{flower('#cdbce3')}</g>
          <g transform="translate(60,95) scale(1.3) rotate(-15)">{flower('#f0c9d3')}</g>
          <g transform="translate(10,10) scale(0.7) rotate(40)">{flower('#c7dcc0')}</g>
          <circle cx="45" cy="60" r="2" fill="#e7b8c2" opacity="0.5" />
          <circle cx="105" cy="20" r="1.6" fill="#cdbce3" opacity="0.5" />
        </pattern>
      </defs>
      <rect width="240" height="240" fill="url(#floralTile)" />
    </svg>
  );
}

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  if (!loading && user) {
    return <Navigate to="/admin/services" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate('/admin/services', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid username or password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-login-shell">
      <FloralBackground />
      <div className="admin-login-card">
        <h1>Our Canvas Memories</h1>
        <p>Sign in to manage services, customers and canvases.</p>
        {error && <div className="admin-alert admin-alert-error">{error}</div>}
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-field">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>
          <div className="admin-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <button type="submit" className="admin-login-btn" disabled={submitting}>
            {submitting ? 'Signing In…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
